import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import { t } from 'i18next';
import { noop } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';

import { NotSpecified } from '~components/Helpers/NotSpecified';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import {
  approveAllWithTracking,
  trackApprovalBatch,
  trackLoadsApproved,
} from '~hooks/useAnalytics/trackApprovals';
import {
  ConfirmDeleteLoadAndTicketsDialog,
  useDeleteLoadAndTickets,
} from '~hooks/useDeleteLoadAndTickets/useDeleteLoadAndTickets';
import useEffectiveCompanyId from '~hooks/useEffectiveCompanyId/useEffectiveCompanyId';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { Load, LoadCycle } from '~hooks/useLoad/models';
import { BasicMaterial } from '~hooks/useMaterials/models';
import { Ticket } from '~hooks/useTickets/models';
import { useTickets } from '~hooks/useTickets/useTickets';
import ApprovalActionsMenu, {
  ApprovedChip,
} from '~pages/Approvals/DriverDay/components/ApprovalActionsMenu';
import TicketDrawerV2 from '~pages/Approvals/Files/components/TicketDrawer/TicketDrawer';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getApprovalStatus } from '~utils/jobs/job-utils';
import { shouldAllowUserToAddNewLoads } from '~utils/loads/load-utils';
import { isViewerAccountCustomer } from '~utils/user/user-utils';

import JobSummaryCard from './JobSummaryCard';
import LoadsTable from './LoadsTable';

interface Props {
  details: Job;
  isEditing?: boolean;
  readOnly: boolean;
  loadsTableNodeRef?: MutableRefObject<HTMLDivElement | undefined>;
  loadsTableSx?: SxProps;
  reload: () => Promise<void>;
  hideJobSummaryCard?: boolean;
  disableApproval?: boolean;
  onInternalNotesChange?: (notes: string) => void;
}

export default observer(function Loads({
  details,
  isEditing = false,
  readOnly,
  loadsTableNodeRef,
  loadsTableSx,
  reload,
  hideJobSummaryCard = false,
  disableApproval = false,
  onInternalNotesChange,
}: Props) {
  const theme = useTheme();

  const { toasterStore, userStore } = useStores();

  const { getAllTicketsByJob, subscribeToTicketUpdates, ticketSubscription } =
    useTickets();
  const [selectedTicket, setSelectedTicket] = useState<{
    isDrawerOpen: boolean;
    loadId?: string;
    ticket?: Ticket;
  }>({ isDrawerOpen: false });

  const companyId = useEffectiveCompanyId();
  const userPermissions = userStore.getPermissions();
  const {
    isApproved: jobApproved,
    isUnapprovable: jobUnapprovable,
    isApprovable: jobApprovable,
  } = getApprovalStatus(details, userPermissions, Boolean(details.order?.editable));

  const [isLoading, setIsLoading] = useState(true);
  const [isApprovingOrUnapprovingJob, setIsApprovingOrUnapprovingJob] = useState(false);
  const [isApprovingAllLoads, setIsApprovingAllLoads] = useState(false);
  const [isAddingNewLoad, setIsAddingNewLoad] = useState(false);
  const [jobLoads, setJobLoads] = useState<Load[]>([]);
  const [jobTickets, setJobTickets] = useState<Ticket[]>([]);
  const {
    addLoad,
    approveJob,
    approveLoad,
    getJobById,
    getJobLoadCycles,
    getJobLoads,
    unapproveJob,
  } = useJob();
  const [cycles, setCycles] = useState<Array<LoadCycle>>([]);
  const isCustomerViewer = isViewerAccountCustomer(userStore, details.order?.account);

  const refreshUIAfterLoadAndTicketsDeleted = async () => {
    await getJobById(details.id, companyId);
    await reload();
  };

  const { deleteLoadOrAlertIfItHasAssociatedTickets, confirmationDialogState } =
    useDeleteLoadAndTickets({
      onLoadDeleted: (loadId) => {
        setJobLoads((state) => state.filter((load) => load.id !== loadId));
        refreshUIAfterLoadAndTicketsDeleted();
      },
      onTicketsDeleted: refreshUIAfterLoadAndTicketsDeleted,
    });

  useEffect(() => {
    if (!ticketSubscription && companyId) {
      subscribeToTicketUpdates();
    }

    return () => {
      ticketSubscription?.unsubscribe();
    };
  }, [ticketSubscription, companyId]);

  const handleSelectedTicketChange = useCallback(
    (loadId: string, ticket?: Ticket) => {
      if (!ticket && !userPermissions.canCreateTicket) {
        return;
      }

      if (ticket && !userPermissions.canEditTicket) {
        return;
      }
      setSelectedTicket({ isDrawerOpen: true, loadId, ticket });
    },
    [userPermissions],
  );

  const handleRemoveLoad = async (loadId: string) => {
    const loadTickets = jobTickets.filter((ticket) => ticket.loadId === loadId);

    const removeSuccess = await deleteLoadOrAlertIfItHasAssociatedTickets(
      loadTickets,
      loadId,
    );

    if (removeSuccess) {
      setJobLoads((state) => state.filter((load) => load.id !== loadId));
    }
  };

  const onAddNew = async () => {
    try {
      setIsAddingNewLoad(true);

      await addLoad(details.id, {
        material: details?.material
          ? ({ id: details.material.id, name: details.material.name } as BasicMaterial)
          : null,
      });

      toasterStore.push(alert(t('loads.load_added'), AlertTypes.success));
      await reload();
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.jobs.failed_to_add_load'));
    } finally {
      setIsAddingNewLoad(false);
    }
  };

  const handleCloseDrawer = () => {
    setSelectedTicket((state) => ({ ...state, isDrawerOpen: false }));
    reload();
  };

  const handleUpdateLoad = useCallback((load: Load) => {
    setJobLoads((state) => state.map((l) => (l.id === load.id ? load : l)));
  }, []);

  useEffect(() => {
    function getInitialData() {
      const loadCyclesRequest = getJobLoadCycles(details.id, companyId).then((res) => {
        setCycles(res);
      });

      const jobLoadsRequest = getJobLoads(details.id).then((response) => {
        setJobLoads(response);
      });

      const jobTicketsRequest = getAllTicketsByJob({ companyId, jobId: details.id }).then(
        (response) => {
          setJobTickets(response);
        },
      );

      return Promise.all([loadCyclesRequest, jobLoadsRequest, jobTicketsRequest]);
    }

    setIsLoading(true);
    getInitialData().then(() => setIsLoading(false));
  }, [details]);

  const allLoadsApprovable = useMemo(() => {
    return !jobLoads.length
      ? false
      : jobLoads.some((load) => {
          const { isApprovable } = getApprovalStatus(
            load,
            userPermissions,
            Boolean(details.order?.editable),
          );
          return isApprovable;
        });
  }, [jobLoads, userPermissions, details.order?.editable]);

  const shouldDisableAddLoadButton = !shouldAllowUserToAddNewLoads(
    details,
    userStore,
    readOnly,
  );

  const onApproveAllLoads = () => {
    setIsApprovingAllLoads(true);

    const approvableLoads = jobLoads.filter(
      (load) =>
        getApprovalStatus(load, userPermissions, Boolean(details.order?.editable))
          .isApprovable,
    );

    approveAllWithTracking(
      approvableLoads,
      (load) => approveLoad(load.id, companyId),
      trackLoadsApproved,
    )
      .then(() => {
        toasterStore.push(alert(t('approvals.loads_approved'), AlertTypes.success));
        reload();
      })
      .finally(() => setIsApprovingAllLoads(false));
  };

  const loadForTicketDrawerV2 = selectedTicket.isDrawerOpen
    ? jobLoads.find((load) => load.id === selectedTicket.loadId)
    : undefined;

  const loadNumberForTicketDrawerV2 = useMemo(() => {
    if (loadForTicketDrawerV2?.id) {
      const loadIndex = jobLoads.findIndex((load) => {
        return load.id === loadForTicketDrawerV2.id;
      });

      if (loadIndex >= 0) {
        return loadIndex + 1;
      }
    }
  }, [loadForTicketDrawerV2, jobLoads]);

  const findTicketForTicketDrawerV2ByLoadId = useCallback(
    (loadId: string) => {
      return jobTickets.find((ticket) => ticket.loadId === loadId);
    },
    [jobTickets],
  );

  const [previousLoadForTicketDrawerV2, nextLoadForTicketDrawerV2] = useMemo(() => {
    if (!selectedTicket.loadId) {
      return [null, null];
    }

    const indexOfCurrentlySelectedLoadWithinAllLoads = jobLoads.findIndex((load) => {
      return selectedTicket.loadId === load.id;
    });

    const previousActionableLoad =
      indexOfCurrentlySelectedLoadWithinAllLoads > 0
        ? (jobLoads
            .slice(0, indexOfCurrentlySelectedLoadWithinAllLoads)
            .reverse()
            .find((load) => {
              const ticket = jobTickets.find(({ loadId }) => loadId === load.id);
              const canOpenDrawerOnLoad = ticket
                ? !isCustomerViewer && userPermissions.canEditTicket
                : !isCustomerViewer && userPermissions.canCreateTicket;

              return canOpenDrawerOnLoad;
            }) ?? null)
        : null;

    const nextActionableLoad =
      indexOfCurrentlySelectedLoadWithinAllLoads < jobLoads.length - 1
        ? (jobLoads.slice(indexOfCurrentlySelectedLoadWithinAllLoads + 1).find((load) => {
            const ticket = jobTickets.find(({ loadId }) => loadId === load.id);
            const canOpenDrawerOnLoad = ticket
              ? !isCustomerViewer && userPermissions.canEditTicket
              : !isCustomerViewer && userPermissions.canCreateTicket;

            return canOpenDrawerOnLoad;
          }) ?? null)
        : null;

    return [previousActionableLoad, nextActionableLoad];
  }, [
    isCustomerViewer,
    jobLoads,
    jobTickets,
    selectedTicket.loadId,
    userPermissions.canCreateTicket,
    userPermissions.canEditTicket,
  ]);

  return (
    <>
      <Box>
        {!hideJobSummaryCard && (
          <>
            <Typography
              sx={{
                color: theme.brandV2.colors.treadBlack,
                fontWeight: 600,
                minHeight: `calc(${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px + ${theme.spacing(0.5)})`,
              }}
            >
              {t('loads.times')}
            </Typography>

            <Box
              sx={{
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                borderRadius: '4px',
                mb: 2,
              }}
            >
              <Box sx={{ px: 1.5, pt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {jobApproved && (
                    <Tooltip title={t('loads.cannot_add_loads_if_job_approved')}>
                      <InfoOutlined sx={{ fontSize: '18px' }} />
                    </Tooltip>
                  )}

                  {userPermissions.canApproveJob && jobUnapprovable && (
                    <ApprovalActionsMenu
                      approved={jobApproved}
                      isLoading={isApprovingOrUnapprovingJob}
                      onMenuItemClick={async () => {
                        try {
                          setIsApprovingOrUnapprovingJob(true);
                          await unapproveJob(details.id, companyId);
                          await reload();
                        } finally {
                          setIsApprovingOrUnapprovingJob(false);
                        }
                      }}
                    />
                  )}

                  {userPermissions.canApproveJob && jobApprovable && (
                    <SmallLoadingButton
                      color="success"
                      loading={isApprovingOrUnapprovingJob}
                      onClick={async () => {
                        try {
                          setIsApprovingOrUnapprovingJob(true);
                          const approvedJob = await approveJob(details.id, companyId);

                          if (approvedJob) {
                            trackApprovalBatch('job', 1);
                          }
                          await reload();
                        } finally {
                          setIsApprovingOrUnapprovingJob(false);
                        }
                      }}
                      sx={(theme) => ({ bgcolor: theme.brandV2.colors.treadGreen })}
                    >
                      {t('actions.approve')}
                    </SmallLoadingButton>
                  )}

                  {jobApproved && !jobUnapprovable && <ApprovedChip hideDropdownArrow />}
                </Box>
              </Box>

              <JobSummaryCard job={details} />
            </Box>
          </>
        )}

        {(details.internalNotes || isEditing) && (
          <>
            <Typography
              sx={{
                color: theme.brandV2.colors.treadBlack,
                fontWeight: 600,
              }}
            >
              {t('form_fields.internal_notes')}
            </Typography>

            {isEditing && onInternalNotesChange ? (
              <TextField
                defaultValue={details.internalNotes ?? ''}
                fullWidth
                multiline
                minRows={2}
                onChange={(e) => onInternalNotesChange(e.target.value)}
                placeholder={t('form_fields.internal_notes_placeholder')}
                size="small"
                sx={{ mt: 1.5, mb: 2 }}
              />
            ) : (
              <Box
                sx={{
                  mt: 1.5,
                  mb: 2,
                  backgroundColor: theme.brandV2.colors.treadGray8,
                  borderRadius: theme.spacing(0.5),
                  p: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color={theme.brandV2.colors.treadGray1}
                  sx={{ whiteSpace: 'pre-line' }}
                >
                  {details.internalNotes}
                </Typography>
              </Box>
            )}
          </>
        )}

        {details.notes && (
          <>
            <Typography
              sx={{
                color: theme.brandV2.colors.treadBlack,
                fontWeight: 600,
              }}
            >
              {t('loads.driver_notes')}
            </Typography>

            <Box
              sx={{
                mt: 1.5,
                mb: 2,
                backgroundColor: theme.brandV2.colors.treadGray8,
                borderRadius: theme.spacing(0.5),
                p: 1,
              }}
            >
              <Typography
                variant="body2"
                color={theme.brandV2.colors.treadGray1}
                sx={{ whiteSpace: 'pre-line' }}
              >
                {details.notes}
              </Typography>
            </Box>
          </>
        )}

        <Box
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Typography
            sx={{
              color: theme.brandV2.colors.treadBlack,
              fontWeight: 600,
              minHeight: `calc(${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px + ${theme.spacing(0.5)})`,
            }}
          >
            {t('approvals.driver_day.loads')}
          </Typography>
          {allLoadsApprovable && (
            <SmallLoadingButton
              color="brandV2Yellow"
              disabled={isEditing || disableApproval}
              loading={isApprovingAllLoads}
              onClick={onApproveAllLoads}
              sx={{
                '&.MuiButtonBase-root': {
                  height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
                },
              }}
            >
              {t('approvals.driver_day.approve_loads')}
            </SmallLoadingButton>
          )}
        </Box>
        <Box ref={loadsTableNodeRef}>
          {jobLoads.length ? (
            <LoadsTable
              cycles={cycles}
              jobTickets={jobTickets}
              loads={jobLoads}
              onLoadCardClick={handleSelectedTicketChange}
              onRemoveLoadClick={handleRemoveLoad}
              reload={reload}
              isEditing={isEditing}
              readonly={isCustomerViewer}
              jobState={details.status}
              updateLoad={handleUpdateLoad}
              disableApproval={disableApproval}
              job={details}
              sx={{
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                borderRadius: '4px',
                p: 1,
                ...loadsTableSx,
              }}
            />
          ) : (
            <NotSpecified />
          )}
        </Box>

        <Box
          sx={{
            mt: 2,
          }}
        >
          <SmallButton
            onClick={onAddNew}
            color={'brandV2Yellow'}
            disabled={
              isLoading || isEditing || isAddingNewLoad || shouldDisableAddLoadButton
            }
          >
            {`+ ${t('loads.add')}`}
          </SmallButton>
        </Box>
      </Box>
      <TicketDrawerV2
        job={details}
        load={loadForTicketDrawerV2}
        loadNumber={loadNumberForTicketDrawerV2}
        onClose={handleCloseDrawer}
        onShowNextTicketClick={
          nextLoadForTicketDrawerV2
            ? () => {
                const nextLoadTicket = findTicketForTicketDrawerV2ByLoadId(
                  nextLoadForTicketDrawerV2.id,
                );
                setSelectedTicket((state) => ({
                  ...state,
                  loadId: nextLoadForTicketDrawerV2.id,
                  ticket: nextLoadTicket,
                }));
              }
            : undefined
        }
        onShowPreviousTicketClick={
          previousLoadForTicketDrawerV2
            ? () => {
                const previousLoadTicket = findTicketForTicketDrawerV2ByLoadId(
                  previousLoadForTicketDrawerV2.id,
                );
                setSelectedTicket((state) => ({
                  ...state,
                  loadId: previousLoadForTicketDrawerV2.id,
                  ticket: previousLoadTicket,
                }));
              }
            : undefined
        }
        refreshLoads={() => {
          getJobLoads(details.id).then((response) => {
            setJobLoads(response);
          });
        }}
        refreshTicket={(ticket, isNewTicket) => {
          setJobTickets((tickets) =>
            tickets.map((jobTicket) => (jobTicket.id === ticket.id ? ticket : jobTicket)),
          );
          setSelectedTicket((state) => {
            if (isNewTicket || state?.ticket?.id === ticket.id) {
              return { ...state, ticket };
            }

            return state;
          });
        }}
        ticket={selectedTicket.ticket}
      />
      <ConfirmDeleteLoadAndTicketsDialog
        isOpen={confirmationDialogState.isOpen}
        onClose={confirmationDialogState.isOpen ? confirmationDialogState.onClose : noop}
        onConfirm={
          confirmationDialogState.isOpen
            ? confirmationDialogState.onConfirm
            : () => Promise.resolve()
        }
      />
    </>
  );
});
